import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Camera, X, ScanLine } from 'lucide-react';

const QrScanner = ({ onScan, onClose }) => {
  const scannerRef = useRef(null);
  const html5QrRef = useRef(null);
  const [error, setError] = useState('');
  const [started, setStarted] = useState(false);
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;

  useEffect(() => {
    const scannerId = 'qr-reader-' + Date.now();
    if (scannerRef.current) {
      scannerRef.current.id = scannerId;
    }

    const html5QrCode = new Html5Qrcode(scannerId);
    html5QrRef.current = html5QrCode;

    html5QrCode.start(
      { facingMode: 'environment' },
      { fps: 10, qrbox: { width: 220, height: 220 }, aspectRatio: 1 },
      (decodedText) => {
        // Phase 2.4.5 — Extract REM ref from URL formats:
        //   https://remobiles.com/r/REM0144  -> 'REM0144'
        //   https://remobiles.com/track/REM0144 -> 'REM0144'
        //   ?ref=REM0144 -> 'REM0144'
        //   plain 'REM0144' -> 'REM0144'
        let ref = decodedText;
        const remMatch = decodedText.match(/\/r\/([Rr][Ee][Mm]\d+)/i)
                      || decodedText.match(/\/track\/([Rr][Ee][Mm]?\d+)/i)
                      || decodedText.match(/[?&]ref=([Rr][Ee][Mm]\d+)/i)
                      || decodedText.match(/^([Rr][Ee][Mm]\d+)$/i);
        if (remMatch) ref = remMatch[1].toUpperCase();

        html5QrCode.stop().catch(() => {});
        onScanRef.current(ref);
      },
      () => {}
    ).then(() => {
      setStarted(true);
    }).catch((err) => {
      setError("Impossible d'acceder a la camera. Verifiez les permissions.");
      console.error('QR Scanner error:', err);
    });

    return () => {
      if (html5QrRef.current && html5QrRef.current.isScanning) {
        html5QrRef.current.stop().catch(() => {});
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="fixed inset-0 bg-black z-50 flex flex-col" data-testid="qr-scanner-overlay">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-black/80">
        <div className="flex items-center gap-2 text-white">
          <ScanLine className="w-5 h-5 text-cyan-400" />
          <span className="text-sm font-semibold">Scanner QR Code</span>
        </div>
        <button
          onClick={() => {
            if (html5QrRef.current && html5QrRef.current.isScanning) {
              html5QrRef.current.stop().catch(() => {});
            }
            onClose();
          }}
          className="p-2 rounded-full bg-white/10 text-white hover:bg-white/20"
          data-testid="qr-scanner-close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Scanner */}
      <div className="flex-1 flex items-center justify-center bg-black relative">
        <div ref={scannerRef} className="w-full max-w-sm" style={{ maxHeight: '70vh' }} />

        {!started && !error && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <Camera className="w-12 h-12 text-cyan-400 mx-auto mb-3 animate-pulse" />
              <p className="text-white text-sm">Demarrage de la camera...</p>
            </div>
          </div>
        )}

        {error && (
          <div className="absolute inset-0 flex items-center justify-center p-6">
            <div className="text-center bg-black/80 rounded-2xl p-6 border border-red-500/30">
              <Camera className="w-10 h-10 text-red-400 mx-auto mb-3" />
              <p className="text-red-400 text-sm font-medium mb-2">{error}</p>
              <p className="text-gray-500 text-xs">Vous pouvez aussi entrer la reference manuellement</p>
              <button
                onClick={onClose}
                className="mt-4 px-6 py-2 bg-white/10 text-white rounded-lg text-sm hover:bg-white/20"
              >
                Fermer
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Footer hint */}
      <div className="px-4 py-3 bg-black/80 text-center">
        <p className="text-gray-400 text-xs">Pointez la camera vers le QR code de la reparation</p>
      </div>
    </div>
  );
};

export default QrScanner;
